import { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { Alert, Button, Col, Form, FormFeedback, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { Spinner } from 'src/components/Common/Spinner';
import { ISetting, ISettingType } from 'src/services/types';
import { useFormik } from 'formik';
import * as Yup from "yup";
import { createSetting, updateSetting } from 'src/services/settings-service';

interface IProps {
    show: boolean;
    onClose: () => void;
    setting: ISetting | null;
    settingTypes: ISettingType[];
    onRefresh: () => void;
} 
export const CreateEditSettingModal = ({ show, onClose, setting, settingTypes, onRefresh }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const formik = useFormik({
        enableReinitialize: true,

        initialValues: {
            settingTypeId: setting?.settingTypeId || '',
            text: setting?.text || '',
        },
        validationSchema: Yup.object().shape({
            settingTypeId: !setting ? Yup.string().required("Please Select Setting Type") : Yup.string().optional(),
            text: Yup.string().required("Please Enter Text"),
        }),
        onSubmit: (values) => {
            if(setting) {
                const { settingTypeId, ...rest } = values;
                onEdit(rest);
            } else {
                onCreate(values);
            }
        }
    });

    const onCreate = async (values: {settingTypeId: string, text: string}) => {
        setLoading(true);
        setErrorMessage('');
        const res = await createSetting(values)
        setLoading(false);
        if (res.result === 'ok') {
            toast('Setting created successfully', { type: 'success'});
            onRefresh();
            onClose();
        } else {
            setErrorMessage(res.message);
            console.log('error:', res.message);
        }
    }

    const onEdit = async (values: {text: string}) => {
        setLoading(true);
        setErrorMessage('');
        const res = await updateSetting(setting?.id!, values)
        setLoading(false);
        if (res.result === 'ok') {
            toast('Setting updated successfully', { type: 'success'});
            onRefresh();
            onClose();
        } else {
            setErrorMessage(res.message);
            console.log('error:', res.message);
        }
    }

    useEffect(() => {
        if(show) {
            formik.resetForm();
        }
    }, [show]);

  return (
    <Modal
        isOpen={show}
        backdrop={"static"}
        id="staticBackdrop"
        className=''
    >
        {
            <div style={{position: 'relative'}}>
                <div className="modal-header">
                    <h5 className="modal-title" id="staticBackdropLabel">
                        {setting ? 'Edit Setting' : 'Create Setting'}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                        aria-label="Close"
                    ></button>
                </div>
                <Form className="needs-validation"
                        onSubmit={(e) => {
                            e.preventDefault();
                            formik.handleSubmit();
                            return false;
                        }}
                >
                    <div className="modal-body">
                        <Row>
                            {
                                !setting ? (
                                    <Col md="6">
                                        <FormGroup className="mb-3">
                                            <Label htmlFor="settingTypeId">Setting Type</Label>
                                            
                                            <select 
                                                className={`form-control ${ formik.touched && formik.errors.settingTypeId ? 'is-invalid' : ''}`}
                                                name="settingTypeId"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.settingTypeId || ""}
                                            >
                                                <option value={''}>Select a option</option>
                                                {
                                                    settingTypes.map((item, index) => (
                                                        <option key={index} value={item.id}>{item.name}</option>
                                                    ))
                                                }
                                            </select>
                                            { formik.touched && formik.errors.settingTypeId ? (
                                                <FormFeedback type="invalid">{formik.errors.settingTypeId}</FormFeedback>
                                            ) : null}
                                        </FormGroup>
                                    </Col>
                                ) : null
                            }
                            <Col md="6">
                                <FormGroup className="mb-3">
                                    <Label htmlFor="text">Text</Label>
                                    <Input
                                        name="text"
                                        placeholder="Text"
                                        type="text"
                                        className="form-control"
                                        id="text"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.text || ""}
                                        invalid={
                                            formik.touched.text && formik.errors.text ? true : false
                                        }
                                    />
                                    {formik.touched.text && formik.errors.text ? (
                                        <FormFeedback type="invalid">{formik.errors.text}</FormFeedback>
                                    ) : null}
                                </FormGroup>
                            </Col>
                        </Row>
                        
                        <div>
                            <Alert color="danger" isOpen={!!errorMessage} toggle={()=>setErrorMessage('')} >
                                {errorMessage}
                            </Alert> 
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onClose}
                        >
                            Close
                        </button>
                        <Button color="primary" type="submit">
                                {setting ? 'Update' : 'Create'}
                            </Button>
                    </div>
                </Form>
                {
                    loading ? 
                    <div 
                        className='d-flex justify-content-center align-items-center rounded-3 my-2 mx-2' 
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(253, 252, 255, 0.75)'}}
                    >
                         <div className="spinner-border text-primary" role="status">
                        </div>
                        <span className="text-primary fs-5 mx-2">
                            {setting ? 'Updating setting...' : 'Creating setting...'}
                        </span>
                    </div>
                    : null
                }
            </div>   
        }
    </Modal>
  )
}
